import { useSelector } from 'react-redux';

import { useCallback } from 'react';
import { useAiSupportApiFacade } from '../../../../apis/facades/useAiSupportApiFacade';
import {
  addInstruction,
  removeInstruction,
  setInstructions,
  updateInstruction as updateInstructionAction,
} from '../../../../reducers/aiSupportReducer';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useDispatch } from 'react-redux';
import { telemetry } from '../../../../telemetry';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { undo } from '../../../../reducers/undoable';
import {
  AiSupportInstructionProperties,
  AiSupportInstructionUpdateableProperties,
} from '@server/ai';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';

export const useInstructions = () => {
  const { get_instructions, post_instructions, put_instructions, delete_instructions } =
    useAiSupportApiFacade();
  const { addNotification } = useSweepNotifications();
  const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';

  const dispatch = useDispatch();

  const fetchInstructions = useCallback(async () => {
    try {
      const instructions = await get_instructions();
      dispatch(setInstructions({ crmOrgId: defaultCrmOrgId, instructions }));
    } catch (error) {
      telemetry.captureError(error);
      dispatch(setInstructions({ crmOrgId: defaultCrmOrgId, instructions: [] }));
    }
  }, [dispatch, get_instructions, defaultCrmOrgId]);

  const createInstruction = useCallback(
    async (instruction: AiSupportInstructionUpdateableProperties) => {
      try {
        const newInstruction = await post_instructions(instruction);
        dispatch(addInstruction({ crmOrgId: defaultCrmOrgId, instruction: newInstruction }));
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: `Failed to create instruction, please try again later`,
          variant: SweepNotificationVariant.Error,
        });
      }
    },
    [dispatch, post_instructions, addNotification, defaultCrmOrgId],
  );

  const updateInstruction = useCallback(
    async (instruction: AiSupportInstructionProperties) => {
      const action = updateInstructionAction({ crmOrgId: defaultCrmOrgId, instruction });
      try {
        dispatch(action);
        await put_instructions(instruction.id, {
          name: instruction.name,
          content: instruction.content,
        });
        dispatch(action);
      } catch (error) {
        telemetry.captureError(error);
        dispatch(undo(action));
        addNotification({
          message: `Failed to update instruction, please try again later`,
          variant: SweepNotificationVariant.Error,
        });
      }
    },
    [dispatch, put_instructions, addNotification, defaultCrmOrgId],
  );

  const deleteInstruction = useCallback(
    async (instructionId: string) => {
      const action = removeInstruction({ crmOrgId: defaultCrmOrgId, instructionId });
      try {
        dispatch(action);
        await delete_instructions(instructionId);
      } catch (error) {
        telemetry.captureError(error);
        dispatch(undo(action));
        addNotification({
          message: `Failed to delete instruction, please try again later`,
          variant: SweepNotificationVariant.Error,
        });
      }
    },
    [dispatch, delete_instructions, addNotification, defaultCrmOrgId],
  );

  return {
    fetchInstructions,
    createInstruction,
    updateInstruction,
    deleteInstruction,
  };
};
